import React, { useEffect, useState } from 'react'

import SafetySystemsModal from './SafetySystemsModal'
import SensorInsights, { StatusBadge, displaySensorName, formatPrice } from './adasInsights'
import { ShieldIcon, ExpandAllIcon, CollapseAllIcon } from './headerIcons'
import { Tooltip } from '../../controls'
import iconHint from '../../../assets/images/icons/icon-hint.png'

const normalizeServiceDisplayName = (name) => {
  if (!name) return ''
  const rawName = String(name).trim()
  if (!rawName) return ''
  const stripped = rawName
    .replace(/\s*[-–]?\s*\(?(oem|aftermarket)\)?\s*$/i, '')
    .trim()
  return stripped || rawName
}

// Only diagnostic scans carry an OEM/Aftermarket choice. The choice lives in the
// operation slug (e.g. pre-repair-scan-oem), so strip the variant to get the base
// id — same list and same derivation as the admin portal's renderServiceRow.
// Without this gate, ANY service that happens to carry a stray scanType picks up
// the suffix: 8 dev rows do, producing "Inspect (Aftermarket)", "Test Drive
// (Aftermarket)" and — worst — "Calibrate (Aftermarket) (dynamic)" (#37422).
const SCAN_OPERATION_IDS = [
  'pre-repair-scan',
  'in-process-scan',
  'post-repair-scan',
  'pre-and-post-repair-scan',
]

const isScanService = (svc) => {
  const id = (svc && svc.operation && svc.operation.id) || ''
  const baseId = String(id).replace(/-(oem|aftermarket)$/, '')
  return SCAN_OPERATION_IDS.indexOf(baseId) !== -1
}

// normalizeServiceDisplayName strips any trailing "(OEM)/(Aftermarket)" from the
// name, so re-derive the scan type — prefer the explicit svc.scanType, else
// recover it from the raw operation name — and re-append it consistently as
// " (OEM)" / " (Aftermarket)".
//
// The admin portal deliberately does NOT append this: it renders an OEM/
// Aftermarket <select> beside the name, so the suffix would be redundant. The
// client portal is read-only and has no selector, so the suffix is the only way
// a client sees which variant was priced. Keep it.
const scanTypeSuffix = (svc) => {
  if (!svc || !isScanService(svc)) return ''
  let type = svc.scanType
  if (!type && svc.operation && svc.operation.name) {
    const match = String(svc.operation.name).match(/\(?(oem|aftermarket)\)?\s*$/i)
    if (match) type = match[1]
  }
  const normalized = String(type || '').trim().toLowerCase()
  if (normalized === 'oem') return ' (OEM)'
  if (normalized === 'aftermarket') return ' (Aftermarket)'
  return ''
}

// The category header shown for a service that carries no categoryName. It MUST
// equal the fallback on the other two surfaces that render this same row: the
// admin portal (AdasReportDetail.js, getServicesBySensor) and the ADAS report
// PDF (AWS/serverless-recon/src/handlers/oldPDF/init.js, the categoryMap build
// in adasVehicleReport), both of which use 'ADAS'. All three apply the same
// not-equipped / collision filter to the same services, so the only thing that
// differed was this literal. The PDF is the artifact the client is actually sent,
// so it is the surface of record — a client comparing their portal against that
// PDF must not see two different category names for one line of work. This
// portal used to say 'Uncategorized', which also split the services into a
// phantom bucket that exists on neither other surface.
//
// Not a defensive guard: dev holds 200 adas services with categoryName: null and
// one with '', across 10 reports. Duplicated per slice on purpose — the slices
// deploy independently, so there is nothing to import.
const DEFAULT_CATEGORY_NAME = 'ADAS'

// Expansion state is keyed by the category/sensor PAIR, because a sensor name is
// only unique WITHIN a category. 18 dev reports have one that spans two —
// "Seatbelt System" under both Safety and ADAS, "Headlight Assembly" under both
// ADAS and Lighting — and with a name-only key those two groups shared one entry,
// so collapsing either collapsed its namesake a screen away. U+001F (unit
// separator) cannot appear in a category or sensor name, unlike '|' or '/'
// ("Wheels/Tires" is a real category).
const GROUP_KEY_SEP = '\u001F'
const GROUP_LIST_SEP = '\u001E'
const groupKeyFor = (categoryName, sensorName) => `${categoryName}${GROUP_KEY_SEP}${sensorName}`

// Calibration operations all share the base name "Calibrate", so the type has to
// come off the service's own calibrationType field. Mirrors serviceDisplayName in
// AWS/serverless-recon/.../elite/adasVehicleReport.ejs and renderServiceRow in
// Web/admin-portal/.../AdasReportDetail.js. Duplicated on purpose: the client
// portal builds and deploys alone, so it cannot import from either.
const CALIBRATION_TYPE_LABELS = { static: 'Static', dynamic: 'Dynamic', dual: 'Dual' }

// Two guards, both load-bearing:
//  - the map lookup means an unrecognised value renders nothing rather than
//    "(Whatever)", and a non-string (the raw adasThink shape is an object) can
//    never reach .charAt() — that would throw and blank the whole report, not
//    one row, because this runs inside render.
//  - the exact "Calibrate" test means the 39 dev services that already store the
//    type in the name never become "Calibrate (Dual) (Dual)".
const calibrationTypeSuffix = (svc, baseName) => {
  if (!svc || baseName !== 'Calibrate') return ''
  const label = CALIBRATION_TYPE_LABELS[
    String(svc.calibrationType == null ? '' : svc.calibrationType).trim().toLowerCase()
  ]
  return label ? ` (${label})` : ''
}
// Which services never render. Source of truth is isExcludedService in
// API/modules/servicePricing/adas/operations.js; DUPLICATED here rather than
// imported because the client portal builds and deploys on its own, so a
// cross-slice import would not exist at runtime (see CLAUDE.md). The ADAS report
// PDF keeps a third copy for the same reason
// (AWS/serverless-recon/src/handlers/oldPDF/adasServiceFilter.js).
//
// #37229: this list used to hold 'not-equipped' alone, so a service the shop had
// explicitly REMOVED still rendered here while the PDF the client is emailed for
// the same report hid it. Keep the three copies in step — drift is what caused
// this, and the client portal is the surface the customer sees.
const EXCLUDED_STATUSES = ['removed', 'not-equipped']
const isExcludedService = svc => !!svc && EXCLUDED_STATUSES.indexOf(svc.recommendedStatus) > -1

const AdasSections = ({ report, allowViewPricing, expandAll, expandNonce, onExpandAll }) => {
  const [showSafety, setShowSafety] = useState(false)
  // Which sections are expanded, keyed by groupKeyFor(category, sensor). This
  // used to live inside each SensorInsights, but the section header — which now
  // pins only while its own section is open — is a SIBLING of that component and
  // could not see it.
  const [openByGroup, setOpenByGroup] = useState({})
  const services = (report.adas && report.adas.services) || []

  const sensorInsights = report.sensorInsights || []
  // Use the same collision flag as the admin portal: the editable, top-level
  // report field (admin toggles it via vehicleInvolvedInCollision), NOT the
  // VIN-decode hint under adas.vinDecodeData — the two can diverge.
  const collision = !!report.vehicleInvolvedInCollision
  const make = ((report.vehicle && report.vehicle.make) || '').toLowerCase()
  const collisionPhrase = make === 'ford' ? 'an accident' : 'a collision'

  // Group by categoryName, then by sensorName — matching admin's filter:
  // hide removed and not-equipped services, and hide collision-only ones when the
  // vehicle was not in a collision.
  //
  // Built above the empty-state early return below so the expansion effects can
  // depend on the resulting group list — hooks must not sit after a return.
  const categories = {}
  services.forEach(svc => {
    if (isExcludedService(svc)) return
    if (svc.collisionRelated && !collision) return
    const cat = svc.categoryName || DEFAULT_CATEGORY_NAME
    if (!categories[cat]) categories[cat] = {}
    const sensor = svc.sensorName || 'General'
    if (!categories[cat][sensor]) categories[cat][sensor] = { services: [] }
    categories[cat][sensor].services.push(svc)
  })

  // Flat list of every rendered group, in order, as category/sensor keys. Joined
  // into a string so the effects below have a stable primitive dependency;
  // `categories` is rebuilt on every render.
  const groupKeys = Object.entries(categories).reduce(
    (keys, [catName, sensors]) => keys.concat(
      Object.keys(sensors).map(sensorName => groupKeyFor(catName, sensorName))
    ),
    []
  )
  const groupKeySignature = groupKeys.join(GROUP_LIST_SEP)

  // Groups default to expanded (#37249). Seeds only the keys that are not in the
  // map yet, rather than latching once on a ref: a group the user collapsed keeps
  // its state because its key is already present, while a group that appears
  // later — a different report on the same mounted component, or a group revealed
  // by new data — arrives expanded like its siblings instead of alone and
  // collapsed. Returning `prev` unchanged when there is nothing to seed is what
  // stops this re-running.
  useEffect(() => {
    if (!groupKeySignature) return
    setOpenByGroup(prev => {
      const keys = groupKeySignature.split(GROUP_LIST_SEP)
      if (keys.every(key => key in prev)) return prev
      const next = { ...prev }
      keys.forEach(key => { if (!(key in next)) next[key] = true })
      return next
    })
  }, [groupKeySignature])

  // Re-apply the parent's Expand/Collapse All broadcast. Keyed on the nonce so
  // pressing the same button twice still re-applies it. Deliberately does NOT
  // depend on groupKeySignature — the seed above owns "the groups changed"; this
  // fires on a button press only.
  useEffect(() => {
    if (!groupKeySignature) return
    const next = {}
    groupKeySignature.split(GROUP_LIST_SEP).forEach(key => { next[key] = !!expandAll })
    setOpenByGroup(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAll, expandNonce])

  const toggleGroup = (groupKey, next) =>
    setOpenByGroup(prev => ({ ...prev, [groupKey]: next }))

  // Figma 18796:156102 / :156106 give the expand/collapse buttons an ACTIVE
  // state, but they are commands rather than a toggle pair, so there is no
  // stored "mode" to read. Derive it: all open, all closed, or neither
  // highlighted when the user has a mixed selection.
  const openCount = groupKeys.filter(key => openByGroup[key]).length
  let expansion = 'mixed'
  if (groupKeys.length) {
    if (openCount === groupKeys.length) expansion = 'all'
    else if (openCount === 0) expansion = 'collapsed'
  }

  if (!services.length) {
    return <p style={{ color: '#69787F', marginTop: 24 }}>No ADAS services on this report.</p>
  }

  return (
    <div className='adas-detail__report-card'>
      <div className='adas-detail__report-header'>
        <div className='adas-detail__report-header-left'>
          <h3 className='adas-detail__report-title'>ADAS Report</h3>
          <label className='adas-detail__collision-check'>
            <input type='checkbox' checked={!!collision} readOnly />
            <span>Vehicle was involved in {collisionPhrase}</span>
          </label>
        </div>
        <div className='adas-detail__report-header-right'>
          <button className='adas-detail__btn-safety' onClick={() => setShowSafety(true)}>
            <ShieldIcon />
            Safety Systems
          </button>
          <span className='adas-detail__header-divider' aria-hidden='true' />
          {/* Expand/collapse all surfaced as icon buttons rather than buried in
              the kebab (#37249), matching the admin portal. The kebab held
              nothing else here, so it is removed rather than left with one
              item. The handlers live in the parent so they also reach the
              Mechanical card, which is a sibling of this one. */}
          <div className='adas-detail__insight-toggles'>
            <button
              type='button'
              className={`adas-detail__insight-toggle${expansion === 'all' ? ' adas-detail__insight-toggle--active' : ''}`}
              onClick={() => onExpandAll(true)}
              title='Expand all insights'
              aria-label='Expand all insights'
              aria-pressed={expansion === 'all'}
            >
              <ExpandAllIcon />
            </button>
            <button
              type='button'
              className={`adas-detail__insight-toggle${expansion === 'collapsed' ? ' adas-detail__insight-toggle--active' : ''}`}
              onClick={() => onExpandAll(false)}
              title='Collapse all insights'
              aria-label='Collapse all insights'
              aria-pressed={expansion === 'collapsed'}
            >
              <CollapseAllIcon />
            </button>
          </div>
        </div>
      </div>

      <div>
        {Object.entries(categories).map(([catName, sensors]) => (
          <div key={catName}>
            {Object.entries(sensors).map(([sensorName, { services: sensorServices }]) => {
              const groupKey = groupKeyFor(catName, sensorName)
              return (
              // The wrapper is the sticky containing block for the header below —
              // it needs the class so the header is pushed out exactly as the
              // next group's header arrives. Matches admin's __sensor-group.
              <div key={groupKey} className='adas-detail__sensor-group'>
                <div
                  className={`adas-detail__group-header${openByGroup[groupKey] ? ' adas-detail__group-header--sticky' : ''}`}
                >
                  <span className='adas-detail__group-category'>{catName}</span>
                  <span className='adas-detail__group-sensor'>{displaySensorName(sensorName)}</span>
                </div>

                {sensorServices.map(svc => {
                  const baseName = normalizeServiceDisplayName(svc.operation ? svc.operation.name : '')
                  // One string, built the same way as the PDF and the admin portal —
                  // not a name plus a loose raw "({calibrationType})" span, which
                  // rendered lowercase and doubled up on already-suffixed names.
                  // An ADAS Map line whose service the shop hasn't chosen yet
                  // (operationOptions, no operation) — the portal is read-only here.
                  const awaitingPick =
                    !baseName && Array.isArray(svc.operationOptions) && svc.operationOptions.length > 0
                  const serviceName = baseName
                    ? baseName + calibrationTypeSuffix(svc, baseName) + scanTypeSuffix(svc)
                    : awaitingPick
                      ? 'Selection required'
                      : '—'
                  return (
                  <div key={svc._id} className='adas-detail__service-row'>
                    {svc.oemName ? (
                      // Same affordance as the admin portal's service row: the
                      // manufacturer's own name for the operation sits behind a
                      // hover, and the hint icon says there is something to hover.
                      // The icon is inside the trigger, so hovering it opens the
                      // same tooltip the name does. Rows without an oemName
                      // (custom services) render the plain name. Focusable, so a
                      // tap on a phone (and the keyboard) opens it through the
                      // focus trigger; a tap elsewhere closes it.
                      <Tooltip
                        placement='top'
                        trigger={['hover', 'focus']}
                        className='adas-detail__service-name-tooltip'
                        // An ADAS Map line's name came from the ADAS Map, not the OEM
                        // (AB#40484; the admin portal and both apps label it the same way).
                        message={`${svc.recommendedSource === 'adasMap' ? 'ADAS Map name' : 'OEM Name'}: ${svc.oemName}`}
                      >
                        <div className='adas-detail__service-name adas-detail__service-name--with-hint' tabIndex={0}>
                          <span>{serviceName}</span>
                          <img className='adas-detail__service-name-hint' src={iconHint} alt='' />
                        </div>
                      </Tooltip>
                    ) : (
                      <div className='adas-detail__service-name'>
                        <span>{serviceName}</span>
                      </div>
                    )}
                    <div className='adas-detail__service-status'>
                      <span className='adas-detail__meta-label'>Status:</span>
                      <StatusBadge status={svc.recommendedStatus} source={svc.recommendedSource} />
                    </div>
                    {allowViewPricing && (
                      <div className='adas-detail__service-price'>
                        <span className='adas-detail__meta-label'>Price:</span>
                        <span className='adas-detail__price-value'>{formatPrice(svc.amount)}</span>
                      </div>
                    )}
                    <span className='adas-detail__service-more'>⋯</span>
                  </div>
                  )
                })}

                <SensorInsights
                  sensorName={sensorName}
                  services={sensorServices}
                  sensorInsights={sensorInsights}
                  report={report}
                  collision={!!collision}
                  open={!!openByGroup[groupKey]}
                  onToggle={next => toggleGroup(groupKey, next)}
                />
              </div>
              )
            })}
          </div>
        ))}
      </div>

      <SafetySystemsModal show={showSafety} onHide={() => setShowSafety(false)} report={report} />
    </div>
  )
}

export default AdasSections
